import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { FirebaseError } from 'firebase/app';
import { cn } from '../lib/utils';
import KodaCareLogoFull from '../components/Custom Icons/KodaCareLogoFull';
import { useLanguage } from '../functions/useLanguage';
import AppButton from '../components/AppButton';

const AUTH_ACTION_URL = 'https://kodacare.pet/auth/action';

export default function ForgotPassword() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, email, {
        url: AUTH_ACTION_URL,
        handleCodeInApp: false,
      });
      setSent(true);
    } catch (err) {
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'auth/user-not-found':
          case 'auth/invalid-email':
            setError(t('forgot.errorNoAccount'));
            break;
          case 'auth/network-request-failed':
            setError(t('forgot.errorNetwork'));
            break;
          default:
            setError(t('forgot.errorGeneric'));
        }
      } else {
        setError(t('forgot.errorGeneric'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn('min-h-screen flex flex-col items-center justify-center p-4', 'bg-background-light')}>
      <div className={cn('w-full max-w-md rounded-3xl overflow-hidden', 'bg-background-light')}>
        {/* Header — identical to Login */}
        <div className={cn('p-12 text-center', 'bg-background-light')}>
          <div className="w-72 h-auto flex items-center justify-center mx-auto mb-0">
            <KodaCareLogoFull height={55} className="w-full h-auto text-primary-brown" />
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {sent ? (
            /* ── Success state ── */
            <div className="text-center space-y-3">
              <h2 className={cn('text-xl font-bold', 'text-text-primary')}>{t('forgot.checkInboxTitle')}</h2>
              <p className={cn('text-sm leading-relaxed', 'text-text-secondary')}>
                {t('forgot.checkInboxBody').replace('{email}', email)}
              </p>
              <p className={cn('text-xs', 'text-gray-400')}>
                {t('forgot.spamHint')}
              </p>
              <Link
                to="/login"
                className={cn('inline-block mt-4 text-sm font-semibold hover:underline', 'text-primary-brown')}
              >
                ← {t('forgot.backToSignIn')}
              </Link>
            </div>
          ) : (
            /* ── Form state ── */
            <>
              <h2 className={cn('text-xl font-bold mb-2', 'text-text-primary')}>{t('forgot.title')}</h2>
              <p className={cn('text-sm mb-6', 'text-text-secondary')}>
                {t('forgot.subtitle')}
              </p>

              {error && (
                <div className="mb-4 p-3 rounded-xl bg-error-red\/5 border border-error-red\/20 text-error-red text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className={cn('block text-sm font-medium mb-1', 'text-text-secondary')}>
                    {t('login.email')}
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={cn('w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40')}
                    placeholder="name@example.com"
                  />
                </div>

                <AppButton
                  type="submit"
                  disabled={isLoading}
                  className="p-4"
                >
                  {isLoading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    t('forgot.sendResetLink')
                  )}
                </AppButton>

                <p className={cn('text-center text-sm', 'text-text-secondary')}>
                  {t('forgot.rememberPassword')}{' '}
                  <Link to="/login" className="font-semibold text-primary-brown hover:underline">
                    {t('login.signIn')}
                  </Link>
                </p>

                <p className={cn('text-center text-xs mt-4', 'text-gray-400')}>
                  {t('forgot.legal')}
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
